/**
 * Main AskAsper chat component
 * Handles landing page and chat interface switching.
 * Uses AgenticChatMessage contract; two-panel layout derives state from messages + loadingMessage.
 */

import React, { useState, useRef, useCallback, useContext, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Select } from 'antd-v5';
import {
    AgenticTwoPanelLayout, wrapSlashTokensInMessage,
    parseAgenticExportDeepLinkTarget,
    decodeAgenticDeepLinkUser,
    encodeAgenticDeepLinkUser,
} from 'asper_design_library';
import type {
    AgenticExportDeepLinkConfig,
    AgenticExportDeepLinkTarget,
    AgenticFeedbackHistoryResponse,
    RightPanelDislikeFeedbackPayload,
    RightPanelFeedbackPayload,
} from 'asper_design_library';
import { UserContext } from 'contexts/ConfigContext';
import { getAuthUserMail, getConfig } from 'utils/helperMethods';
import { ConfigKeys } from 'utils/Enums';
import { trackEventMixpanel } from 'utils/mixpanel';
import {
    ASK_ASPER_ARTEFACT_EXPORT_OPTION_CLICK,
    ASK_ASPER_MULTI_SELECT_EXPORT_OPTION_CLICK,
} from 'utils/mixpanelEventNames';
import {
    startAskAsperSession,
    endAskAsperSession,
    scheduleAskAsperSessionEndOnNavigate,
    recordAskAsperThreadAccessed,
    recordAskAsperQuestionAsked,
    recordAskAsperExport,
    updateAskAsperThreadTitle,
    ASK_ASPER_SESSION_END_REASON,
} from './utils/askAsperSessionTracking';
import { LandingPage } from './components/LandingPage';
import { withDisplayEntityTypeLabels } from './utils/lookupDimensionGroups';
import { buildClarificationResumeMessages } from './utils/clarificationResumeMessage';
import { listenToSSE } from './services/sseService';
import { fetchLookupColumns, type LookupItem } from './services/lookupColumnsService';
import Loader from '../../../molecules/Loader';
import {
    createStreamingPlaceholder,
    updateLoadingMessage,
    transformChatHistory,
} from './services/messageProcessor';
import {
    getChatHistory,
    getFeedbackHistory,
    mapChatSessionsToThreads,
    deleteThread,
    renameThread,
    buildFeedbackUpdateFromRightPanelPayload,
    updateAgenticFeedback,
} from './services/chatHistoryService';
import {
    getCustomQuestions,
    createCustomQuestion,
    updateCustomQuestion,
    deleteCustomQuestion,
    type CustomQuestionBankResponse,
} from './services/customQuestionService';
import {
    getQuestionBankData,
    type QuestionBankCategory,
    toQuestionBankApiFormat,
} from './services/questionBankService';
import { useConversationHistory } from './hooks/useConversationHistory';
import type { AgenticChatMessage, ApiHistoryMessage, CustomQuestionDisplay } from './types';
import type { SSERawPayload } from './types';
import { exportArtifactAsExcel } from './services/exportService';
import { listTenants, syncPromptContext } from './services/promptService';
import type { Tenant } from './prompts/types';
import { AddToEvalButton } from './eval/AddToEvalButton';
import { notifyAskAsperApiError, notifyAskAsperArtifactUnavailable } from './utils/notifyApiError';


const ASK_ASPER_CHAT_SEARCH_DISCLAIMER =
    'Ask Asper uses your enterprise data securely. Review outputs before acting on critical decisions.';

// State retention keys
const ACTIVE_THREAD_ID_KEY = 'AskAsper3_ActiveThreadId';
const SELECTED_THREAD_TITLE_KEY = 'AskAsper3_SelectedThreadTitle';
const SELECTED_PROMPT_BRANCH_KEY = 'AskAsper3_SelectedPromptBranch';
const ASK_ASPER_ROUTE = '/ask-asper';

const buildExportDeepLinkConfig = (encodedUserId?: string): AgenticExportDeepLinkConfig => ({
    hostLink: typeof window !== 'undefined' ? window.location.origin : '',
    endpoint: ASK_ASPER_ROUTE,
    encodedUserId,
});

// Helper functions for state retention
const setAskAsperStoredState = (key: string, value: string | undefined) => {
    if (value !== undefined) {
        localStorage.setItem(key, value);
    } else {
        localStorage.removeItem(key);
    }
};

const getAskAsperStoredState = (key: string): string | undefined => {
    const value = localStorage.getItem(key);
    return value || undefined;
};

const evalNavStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: '#fff',
    border: '1px solid #d9d9d9',
    borderRadius: 6,
    padding: '4px 12px',
    cursor: 'pointer',
    fontSize: 13,
    color: '#595959',
    fontFamily: 'inherit',
    lineHeight: 1.4,
};

interface AskAsperProps {
    /** When true, the eval platform UI (prompt branch selector, eval sets, add-to-eval) is shown. */
    isEvalEnabled?: boolean;
}

export const AskAsper: React.FC<AskAsperProps> = ({ isEvalEnabled = false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);
    /** Same source as `enableAskAsper`; entityTypes are Title Cased for UI (brand_family → Brand Family). */
    const lookupDimensionGroups = useMemo(
        () => withDisplayEntityTypeLabels(commonConfig?.lookupDimensionGroups),
        [commonConfig?.lookupDimensionGroups],
    );
    const userEmail = getAuthUserMail() || localStorage.getItem('email') || '';
    const exportDeepLinkTarget = useMemo(
        () => parseAgenticExportDeepLinkTarget(searchParams),
        [searchParams],
    );
    // Deep links opened by a different user (encoded userId param doesn't match the
    // logged-in email) are not theirs: surface a toast and fall back to a new chat.
    const deepLinkUserMismatch = useMemo(() => {
        if (!exportDeepLinkTarget?.encodedUserId) return false;
        const linkOwner = decodeAgenticDeepLinkUser(exportDeepLinkTarget.encodedUserId);
        return !!linkOwner && !!userEmail && linkOwner.toLowerCase() !== userEmail.toLowerCase();
    }, [exportDeepLinkTarget, userEmail]);
    const effectiveDeepLinkTarget = deepLinkUserMismatch ? null : exportDeepLinkTarget;
    const exportDeepLink = useMemo(
        () => buildExportDeepLinkConfig(encodeAgenticDeepLinkUser(userEmail)),
        [userEmail],
    );
    /** Completed turns (each item = one Q&A with user_input + content/charts/steps) */
    const [messages, setMessages] = useState<AgenticChatMessage[]>([]);
    /** Current streaming turn; layout derives running tasks, phases, accordion from this */
    const [loadingMessage, setLoadingMessage] = useState<AgenticChatMessage | null>(null);
    const [searchValue, setSearchValue] = useState('');
    /** Lookup items eagerly loaded from /lookup-columns (with 1-week localStorage cache) for the
     *  `/` slash-command dropdown. The page renders only after these items are available. */
    const [lookupItems, setLookupItems] = useState<LookupItem[]>([]);
    /** True once the lookup-columns fetch has completed (or failed) — gates the page render. */
    const [lookupsReady, setLookupsReady] = useState(false);
    /** (column_name, entity_name) pairs the user has inserted via the slash-command lookup
     *  in the current draft; sent as the structured `entities` field with the next SSE call. */
    const [selectedLookups, setSelectedLookups] = useState<LookupItem[]>([]);
    const [activeThreadId, setActiveThreadId] = useState<string | undefined>(undefined);
    const [selectedThreadTitle, setSelectedThreadTitle] = useState<string | undefined>(undefined);
    const [viewMode, setViewMode] = useState<'list' | 'presentation'>('list');
    const [customQuestions, setCustomQuestions] = useState<CustomQuestionBankResponse[]>([]);
    const [questionBankCategories, setQuestionBankCategories] = useState<QuestionBankCategory[]>([]);
    /** Loaded when opening a thread so the layout can show prior like/dislike on accordions */
    const [feedbackHistory, setFeedbackHistory] = useState<AgenticFeedbackHistoryResponse | undefined>();
    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [selectedTenant, setSelectedTenant] = useState<string | undefined>();
    const [promptSyncing, setPromptSyncing] = useState(false);
    const {
        details: chatSessions,
        loading: sidebarThreadsLoading,
        refetch: refetchSessions,
        error: sessionsFetchError,
    } = useConversationHistory();
    const threads = mapChatSessionsToThreads(chatSessions);

    useEffect(() => {
        if (sessionsFetchError) notifyAskAsperApiError(sessionsFetchError);
    }, [sessionsFetchError]);

    const loadingMessageRef = useRef<AgenticChatMessage | null>(null);
    const activeThreadIdRef = useRef(activeThreadId);
    const hasRestoredRef = useRef(false);
    const selectedTenantRef = useRef<string | undefined>(undefined);
    const promptCommitShaByBranchRef = useRef<Record<string, string>>({});
    const promptSyncPromiseRef = useRef<Promise<void> | null>(null);
    const promptSyncGenerationRef = useRef(0);

    useEffect(() => {
        activeThreadIdRef.current = activeThreadId;
    }, [activeThreadId]);

    // Ask Asper session lifecycle. Starts on mount; ends once (single-fire guard in the
    // tracker) on browser/tab close (pagehide) or in-app navigation away (unmount).
    // Manual logout and auth-timeout are handled centrally (App.tsx / helperMethods) so
    // those exits attribute the correct end_reason before this navigate fallback runs.
    useEffect(() => {
        startAskAsperSession();

        // pagehide is the reliable modern unload signal (fires on tab/browser close and is
        // bfcache-compatible, unlike beforeunload/unload). keepalive keeps the request alive.
        const handlePageHide = () => endAskAsperSession(ASK_ASPER_SESSION_END_REASON.BROWSER_CLOSE);
        window.addEventListener('pagehide', handlePageHide);

        return () => {
            window.removeEventListener('pagehide', handlePageHide);
            // Deferred so a React Strict Mode remount (dev) cancels this instead of ending
            // the session spuriously; a real route change lets it fire with `navigate`.
            scheduleAskAsperSessionEndOnNavigate();
        };
    }, []);

    const isStreaming = loadingMessage != null;
    const showLandingPage = messages.length === 0 && !loadingMessage && !effectiveDeepLinkTarget;

    const runPromptSync = useCallback((tenant?: string): Promise<void> => {
        if (!isEvalEnabled) {
            return Promise.resolve();
        }

        const syncTenant = tenant || undefined;
        const branchKey = syncTenant ?? '';
        const generation = promptSyncGenerationRef.current + 1;
        promptSyncGenerationRef.current = generation;
        setPromptSyncing(true);

        const syncPromise = syncPromptContext(syncTenant)
            .then((response) => {
                if (promptSyncGenerationRef.current !== generation) return;
                selectedTenantRef.current = syncTenant;
                if (response.commit_sha) {
                    promptCommitShaByBranchRef.current[branchKey] = response.commit_sha;
                } else {
                    delete promptCommitShaByBranchRef.current[branchKey];
                }
            })
            .catch((error: unknown) => {
                if (promptSyncGenerationRef.current === generation) {
                    delete promptCommitShaByBranchRef.current[branchKey];
                    notifyAskAsperApiError(error);
                }
                throw error;
            })
            .finally(() => {
                if (promptSyncGenerationRef.current !== generation) return;
                setPromptSyncing(false);
                if (promptSyncPromiseRef.current === syncPromise) {
                    promptSyncPromiseRef.current = null;
                }
            });

        promptSyncPromiseRef.current = syncPromise;
        return syncPromise;
    }, [isEvalEnabled]);

    const getUserNameFromEmail = (email: string) => {
        const name = email.split('.')[0].charAt(0).toUpperCase() + email.split('.')[0].slice(1);
        return name.includes('@') ? '' : name;
    };

    const userName = getUserNameFromEmail(userEmail) || '';

    const fetchCustomQuestions = useCallback(async () => {
        if (!userEmail) return;
        try {
            const data = await getCustomQuestions(userEmail);
            setCustomQuestions(data);
        } catch (error) {
            console.error('Failed to fetch custom questions:', error);
            notifyAskAsperApiError(error);
            setCustomQuestions([]);
        }
    }, [userEmail]);

    const fetchQuestionBankData = useCallback(async () => {
        try {
            const data = await getQuestionBankData();
            setQuestionBankCategories(data);
        } catch (error) {
            console.error('Failed to fetch question bank data:', error);
            notifyAskAsperApiError(error);
            setQuestionBankCategories([]);
        }
    }, []);

    /** Eagerly fetch the lookup-columns list on mount. The service uses a 1-week
     *  localStorage cache so warm mounts resolve synchronously after the first load.
     *  The page render is gated on this completing (success or failure) so the input
     *  is never shown without its suggestion data. */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const items = await fetchLookupColumns();
                if (!cancelled) setLookupItems(items);
            } catch (error) {
                console.error('Failed to fetch lookup columns:', error);
                notifyAskAsperApiError(error);
            } finally {
                if (!cancelled) setLookupsReady(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        fetchCustomQuestions();
    }, [fetchCustomQuestions]);

    useEffect(() => {
        fetchQuestionBankData();
    }, [fetchQuestionBankData]);

    useEffect(() => {
        if (!isEvalEnabled) return;
        listTenants().then((response) => setTenants(response.tenants)).catch(() => {});
    }, [isEvalEnabled]);

    // Debug: log when question bank data changes (dev only)
    useEffect(() => {
        if (process.env.NODE_ENV === 'development' && questionBankCategories.length > 0) {
            const total = questionBankCategories.reduce((sum, c) => sum + c.questions.length, 0);
            console.log('[AskAsper] Question bank loaded:', questionBankCategories.length, 'categories,', total, 'questions');
        }
    }, [questionBankCategories]);

    const transformedCustomQuestions = useMemo<CustomQuestionDisplay[]>(
        () =>
            customQuestions.map((q) => ({
                id: q.QuestionID.toString(),
                text: q.Question,
            })),
        [customQuestions],
    );

    const handleAddCustomQuestion = useCallback(
        async (question: { id: string; text: string }) => {
            try {
                await createCustomQuestion({
                    QuestionID: question.id ? parseInt(question.id, 10) : undefined,
                    UserID: userEmail,
                    CustomerID: '-',
                    Question: question.text,
                });
                await fetchCustomQuestions();
            } catch (error) {
                console.error('Failed to add custom question:', error);
                notifyAskAsperApiError(error);
            }
        },
        [userEmail, fetchCustomQuestions],
    );

    const handleEditCustomQuestion = useCallback(
        async (question: { id?: string | number; text?: string; QuestionID?: number; Question?: string }) => {
            try {
                if (!question) return;

                let questionId: number | null = null;
                let questionText: string | undefined;

                if (question.QuestionID !== undefined && question.QuestionID !== null) {
                    questionId =
                        typeof question.QuestionID === 'number'
                            ? question.QuestionID
                            : parseInt(String(question.QuestionID), 10);
                    questionText = question.Question;
                } else if (question.id !== undefined && question.id !== null) {
                    if (typeof question.id === 'number') {
                        questionId = question.id;
                    } else if (typeof question.id === 'string') {
                        const idStr = question.id.trim();
                        if (idStr === '' || idStr === 'null' || idStr === 'undefined') return;
                        questionId = parseInt(idStr, 10);
                    }
                    questionText = question.text;
                }

                if (questionId === null || isNaN(questionId) || questionId <= 0) return;

                if (!questionText || questionText.trim() === '') {
                    const existingQuestion = customQuestions.find((q) => q.QuestionID === questionId);
                    if (existingQuestion?.Question) {
                        questionText = existingQuestion.Question;
                    } else return;
                }

                await updateCustomQuestion({
                    QuestionID: questionId,
                    UserID: userEmail,
                    CustomerID: '-',
                    Question: questionText.trim(),
                });
                await fetchCustomQuestions();
            } catch (error) {
                console.error('Failed to edit custom question:', error);
                notifyAskAsperApiError(error);
            }
        },
        [userEmail, fetchCustomQuestions, customQuestions],
    );

    const handleDeleteCustomQuestion = useCallback(
        async (questionId: string) => {
            try {
                await deleteCustomQuestion({
                    QuestionID: parseInt(questionId, 10),
                    UserID: userEmail,
                });
                await fetchCustomQuestions();
            } catch (error) {
                console.error('Failed to delete custom question:', error);
                notifyAskAsperApiError(error);
            }
        },
        [userEmail, fetchCustomQuestions],
    );

    const handleArtifactExport = useCallback(
        (format: 'docx' | 'xlsx' | 'pdf' | 'ppt', run_id?: string, session_id?: string) => {
            if (format !== 'xlsx' || !run_id || !session_id) return;
            const runExport = () => {
                exportArtifactAsExcel(session_id, run_id, `artifact-${run_id}.xlsx`).catch((error) => {
                    console.error('Failed to export artifact as Excel:', error);
                    notifyAskAsperApiError(error, { context: 'export', onRetry: runExport });
                });
            };
            runExport();
        },
        [],
    );

    const handleMultiSelectExport = useCallback(
        (format: 'docx' | 'xlsx' | 'pdf' | 'ppt', selectedIds: string[]) => {
            if (format !== 'xlsx' || selectedIds.length === 0 || !activeThreadId) return;
            const sessionId = activeThreadId;
            const runExport = () => {
                Promise.all(
                    selectedIds.map((runId) =>
                        exportArtifactAsExcel(sessionId, runId, `artifact-${runId}.xlsx`),
                    ),
                ).catch((error) => {
                    console.error('Failed to export artifacts as Excel:', error);
                    notifyAskAsperApiError(error, { context: 'export', onRetry: runExport });
                });
            };
            runExport();
        },
        [activeThreadId],
    );

    // Design-library fires export-option Mixpanel events for every format (client-side Word/PDF/PPT
    // and Excel current view never call onArtifactExport). Count those here so exports_count stays accurate.
    const handleAskAsperTrackEvent = useCallback((eventName: string, eventProperty: Object = {}) => {
        if (eventName === ASK_ASPER_ARTEFACT_EXPORT_OPTION_CLICK) {
            recordAskAsperExport(1);
        } else if (eventName === ASK_ASPER_MULTI_SELECT_EXPORT_OPTION_CLICK) {
            const selectedCount = (eventProperty as { selected_count?: number }).selected_count;
            recordAskAsperExport(
                typeof selectedCount === 'number' && selectedCount > 0 ? selectedCount : 1,
            );
        }
        trackEventMixpanel(eventName, eventProperty);
    }, []);

    const waitForPromptSyncIfNeeded = useCallback(async () => {
        if (!isEvalEnabled || !promptSyncPromiseRef.current) return;
        await promptSyncPromiseRef.current;
    }, [isEvalEnabled]);

    const loadFeedbackHistory = useCallback(async (threadId: string) => {
        try {
            const feedback = await getFeedbackHistory(threadId);
            if (activeThreadIdRef.current === threadId) {
                setFeedbackHistory(feedback);
            }
        } catch (error) {
            console.error('Failed to load feedback history:', error);
        }
    }, []);

    const submitAgenticFeedback = useCallback(
        async (
            payload: RightPanelFeedbackPayload,
            sentiment: 'like' | 'dislike',
            subjectiveAnswer?: string | null,
            files?: File[],
        ) => {
            const thumbs_up = sentiment === 'like' ? ('like' as const) : ('dislike' as const);
            const body = buildFeedbackUpdateFromRightPanelPayload(
                payload,
                thumbs_up,
                subjectiveAnswer ?? '',
                files,
                payload.session_id ?? activeThreadIdRef.current ?? undefined,
            );
            if (!body) {
                console.warn('Cannot submit agentic feedback: missing run_id on payload');
                return;
            }
            try {
                await updateAgenticFeedback(body);
                const tid = activeThreadIdRef.current;
                if (tid) {
                    await loadFeedbackHistory(tid);
                }
            } catch (error) {
                console.error('Failed to submit agentic feedback:', error);
                notifyAskAsperApiError(error);
            }
        },
        [loadFeedbackHistory],
    );

    const handleRightPanelThumbsUp = useCallback(
        (payload: RightPanelFeedbackPayload) => {
            void submitAgenticFeedback(payload, 'like');
        },
        [submitAgenticFeedback],
    );

    const handleRightPanelThumbsDown = useCallback(
        (payload: RightPanelFeedbackPayload) => {
            void submitAgenticFeedback(payload, 'dislike');
        },
        [submitAgenticFeedback],
    );

    const handleRightPanelDislikeFeedbackSubmit = useCallback(
        (payload: RightPanelDislikeFeedbackPayload) => {
            void submitAgenticFeedback(payload, 'dislike', payload.feedback ?? '', payload.files);
        },
        [submitAgenticFeedback],
    );

    const attachSseHandlers = useCallback(() => {
        const handleMessage = (json: unknown) => {
            const payload = json as SSERawPayload & { type?: string };
            if (payload.type === 'error') {
                setLoadingMessage((prev) => {
                    if (!prev) return null;
                    const updated = updateLoadingMessage(prev, payload as SSERawPayload);
                    setMessages((m) => [...m, updated]);
                    return null;
                });
                loadingMessageRef.current = null;
                refetchSessions();
                return;
            }
            setLoadingMessage((prev) => {
                // Use ref as base when available so rapid events (viz_generation_end, table_generation_end,
                // task_complete) always apply on top of the latest accumulated state. Prevents race where
                // React batches updates and a later callback receives stale prev, overwriting earlier changes.
                const base = loadingMessageRef.current ?? prev;
                const updated = base ? updateLoadingMessage(base, json as SSERawPayload) : null;
                loadingMessageRef.current = updated;
                return updated;
            });
        };

        const handleDone = () => {
            const toAdd = loadingMessageRef.current;
            if (toAdd) setMessages((prev) => [...prev, toAdd]);
            setLoadingMessage(null);
            loadingMessageRef.current = null;
            refetchSessions();
        };

        const handleError = (err: unknown) => {
            console.error('SSE error:', err);
            notifyAskAsperApiError(err);
            setLoadingMessage(null);
            loadingMessageRef.current = null;
        };

        return { handleMessage, handleDone, handleError };
    }, [refetchSessions]);

    const applyClarificationResolved = useCallback(
        (
            msg: AgenticChatMessage,
            messageId: string | number | undefined,
            selected: Array<{ column_name: string; entity_name: string }>,
            /** Picks keyed by the ambiguous term they answer, so a resolved card re-renders them. */
            selectionsByTerm?: Record<string, { column_name: string; entity_name: string }>,
            /** Entity names the user chose to ignore, so the resolved card strikes them through. */
            ignoredTerms?: string[],
        ): AgenticChatMessage | null => {
            const hasClarification =
                msg.additional_kwargs?.interrupt === true &&
                Array.isArray(msg.additional_kwargs?.needs_clarification) &&
                msg.additional_kwargs.needs_clarification.length > 0;
            if (!hasClarification) return null;

            if (messageId != null) {
                if (msg.id !== messageId && msg.run_id !== messageId) return null;
            } else if (msg.additional_kwargs?.clarification_resolved) {
                return null;
            }

            return {
                ...msg,
                additional_kwargs: {
                    ...msg.additional_kwargs,
                    selected_entities: selected,
                    ...(selectionsByTerm && Object.keys(selectionsByTerm).length > 0
                        ? { clarification_selections: selectionsByTerm }
                        : {}),
                    ...(ignoredTerms && ignoredTerms.length > 0
                        ? { clarification_ignored: ignoredTerms }
                        : {}),
                    clarification_resolved: true,
                },
            };
        },
        [],
    );

    const markClarificationResolved = useCallback(
        (
            messageId: string | number | undefined,
            selected: Array<{ column_name: string; entity_name: string }>,
            selectionsByTerm?: Record<string, { column_name: string; entity_name: string }>,
            ignoredTerms?: string[],
        ) => {
            setLoadingMessage((prev) => {
                if (!prev) return prev;
                return (
                    applyClarificationResolved(
                        prev,
                        messageId,
                        selected,
                        selectionsByTerm,
                        ignoredTerms,
                    ) ?? prev
                );
            });
            setMessages((prev) => {
                let updated = false;
                const next = prev.map((m) => {
                    if (updated) return m;
                    const resolved = applyClarificationResolved(
                        m,
                        messageId,
                        selected,
                        selectionsByTerm,
                        ignoredTerms,
                    );
                    if (resolved) {
                        updated = true;
                        return resolved;
                    }
                    return m;
                });
                if (updated) return next;
                // Fall back: last unresolved clarification interrupt
                for (let i = prev.length - 1; i >= 0; i--) {
                    const resolved = applyClarificationResolved(
                        prev[i],
                        undefined,
                        selected,
                        selectionsByTerm,
                        ignoredTerms,
                    );
                    if (resolved) {
                        const copy = [...prev];
                        copy[i] = resolved;
                        return copy;
                    }
                }
                return prev;
            });
        },
        [applyClarificationResolved],
    );
    const resumeFromClarification = useCallback(
        async (
            selected: Array<{ column_name: string; entity_name: string }>,
            messageId?: string | number,
            /** Picks keyed by the ambiguous term they answer (from the disambiguation card). */
            selectionsByTerm?: Record<string, { column_name: string; entity_name: string }>,
            /** Entity names the user dismissed via Ignore on the card. */
            ignoredTerms?: string[],
        ) => {
            if (isStreaming || !activeThreadId) return;

            try {
                await waitForPromptSyncIfNeeded();
            } catch {
                return;
            }

            // Finalize the interrupt turn into messages (resolved) before starting the resume stream.
            const currentLoading = loadingMessageRef.current;
            const resolvedLoading =
                currentLoading != null
                    ? applyClarificationResolved(
                          currentLoading,
                          messageId,
                          selected,
                          selectionsByTerm,
                          ignoredTerms,
                      )
                    : null;
            if (resolvedLoading) {
                setMessages((prev) => [...prev, resolvedLoading]);
                setLoadingMessage(null);
                loadingMessageRef.current = null;
            } else {
                markClarificationResolved(messageId, selected, selectionsByTerm, ignoredTerms);
            }

            const tenantForRequest = isEvalEnabled ? selectedTenantRef.current : undefined;
            recordAskAsperThreadAccessed(activeThreadId, selectedThreadTitle);

            // `selected` is the full set the card resumes with: entities the backend already
            // matched plus the user's picks for the ambiguous terms. The question shown to the user
            // is just that final entity list joined with "and" (e.g. "CORONA and US") — not the
            // original question or the design-lib clarified query. Synthetic "proceed" only on skip.
            // When terms were ignored, the API gets a longer instruction while the displayed
            // question stays the plain entity list.
            const selectedNames = Array.from(
                new Set(selected.map((s) => s.entity_name?.trim()).filter(Boolean) as string[]),
            );
            const ignoredNames = Array.from(
                new Set((ignoredTerms ?? []).map((n) => n?.trim()).filter(Boolean) as string[]),
            );
            const { displayMessage, apiMessage } = buildClarificationResumeMessages(
                selectedNames,
                ignoredNames,
            );

            // The question is shown by the streaming placeholder below, so leave the composer empty.
            setSearchValue('');

            // Use empty placeholder user_input only for skip/"proceed"; otherwise show the entity list.
            const placeholder = createStreamingPlaceholder(
                displayMessage === 'proceed' ? '' : displayMessage,
                activeThreadId,
            );
            setLoadingMessage(placeholder);
            loadingMessageRef.current = placeholder;
            const { handleMessage, handleDone, handleError } = attachSseHandlers();
            listenToSSE(
                {
                    message: apiMessage,
                    thread_id: activeThreadId,
                    source: 'EnterpriseData',
                    new_session: false,
                    tenant: tenantForRequest || undefined,
                    selected_entities: selected,
                },
                handleMessage,
                handleDone,
                handleError,
            );
        },
        [
            isStreaming,
            activeThreadId,
            selectedThreadTitle,
            isEvalEnabled,
            waitForPromptSyncIfNeeded,
            applyClarificationResolved,
            markClarificationResolved,
            attachSseHandlers,
        ],
    );

    const handleEntityDisambiguationContinue = useCallback(
        (
            selected: Array<{ column_name: string; entity_name: string }>,
            messageId?: string | number,
            /** Design-lib clarified query — unused: the resume question is the selected entity list. */
            _resolvedUserInput?: string,
            selectionsByTerm?: Record<string, { column_name: string; entity_name: string }>,
            ignoredTerms?: string[],
        ) => {
            void resumeFromClarification(selected, messageId, selectionsByTerm, ignoredTerms);
        },
        [resumeFromClarification],
    );

    const handleEntityDisambiguationSkip = useCallback(
        (messageId?: string | number) => {
            void resumeFromClarification([], messageId);
        },
        [resumeFromClarification],
    );

    const handleSearchSubmit = useCallback(
        async (query: string) => {
            if (!query.trim() || isStreaming) return;

            const tenantForRequest = isEvalEnabled ? selectedTenantRef.current : undefined;
            const isNewSession = !activeThreadId;

            try {
                await waitForPromptSyncIfNeeded();
            } catch {
                setSearchValue(query);
                return;
            }

            const threadIdForRequest = activeThreadId ?? crypto.randomUUID();
            const promptCommitSha = isEvalEnabled
                ? promptCommitShaByBranchRef.current[tenantForRequest ?? '']
                : undefined;

            // Session metrics: count every submitted question and register the thread.
            recordAskAsperQuestionAsked();
            if (isNewSession) {
                recordAskAsperThreadAccessed(threadIdForRequest, undefined, { isNew: true });
                setActiveThreadId(threadIdForRequest);
                setSelectedThreadTitle(undefined);
            } else {
                recordAskAsperThreadAccessed(threadIdForRequest, selectedThreadTitle);
            }

            // Snapshot the lookups inserted via the slash-command dropdown for this
            // submission. Confirmed picks live in the textarea as the bare entity name
            // (no leading `/`), so we filter against the entity name itself.
            const entitiesForRequest = selectedLookups
                .filter((l) => query.includes(l.entity_name))
                .map(({ column_name, entity_name }) => ({ column_name, entity_name }));

            // Wrap each selected entity name with inline `/*EntityName*/` markers so the
            // backend can recognise entity spans within the free-text message; the chat
            // view strips these markers (and highlights the entity) so the user sees
            // natural text.
            const wrappedMessage = wrapSlashTokensInMessage(
                query,
                entitiesForRequest.map((e) => e.entity_name),
            );

            const placeholder = createStreamingPlaceholder(
                wrappedMessage,
                threadIdForRequest
            );
            setLoadingMessage(placeholder);
            loadingMessageRef.current = placeholder;
            setSearchValue('');

            setSelectedLookups([]);

            const params = {
                message: wrappedMessage,
                thread_id: threadIdForRequest,
                source: 'EnterpriseData',
                new_session: isNewSession,
                tenant: tenantForRequest || undefined,
                prompt_commit_sha: promptCommitSha || undefined,
                entities: entitiesForRequest.length > 0 ? entitiesForRequest : undefined,
            };

            const { handleMessage, handleDone, handleError } = attachSseHandlers();
            listenToSSE(params, handleMessage, handleDone, handleError);
        },
        [
            isStreaming,
            activeThreadId,
            selectedThreadTitle,
            isEvalEnabled,
            waitForPromptSyncIfNeeded,
            selectedLookups,
            attachSseHandlers,
        ]
    );
    // Keep ref in sync for handleDone (closure over latest loadingMessage)
    React.useEffect(() => {
        loadingMessageRef.current = loadingMessage;
    }, [loadingMessage]);

    /**
     * The landing page search (AI Mode) routes here with the question in route state. Ask it once
     * the lookups the submit path depends on are loaded, then drop it from history so going back
     * or refreshing does not re-ask it.
     */
    const prefillAskedRef = useRef(false);
    useEffect(() => {
        const prefillQuestion = (location.state as { prefillQuestion?: string } | null)?.prefillQuestion;
        if (!prefillQuestion || !lookupsReady || prefillAskedRef.current) return;
        prefillAskedRef.current = true;
        navigate(ASK_ASPER_ROUTE, { replace: true });
        void handleSearchSubmit(prefillQuestion);
    }, [location.state, lookupsReady, navigate, handleSearchSubmit]);

    // Restore stored state on mount
    useEffect(() => {
        if (isEvalEnabled) {
            const savedPromptBranch = getAskAsperStoredState(SELECTED_PROMPT_BRANCH_KEY);
            setSelectedTenant(savedPromptBranch);
            selectedTenantRef.current = savedPromptBranch;
            runPromptSync(savedPromptBranch).catch(() => {});
        }

        const savedThreadId = getAskAsperStoredState(ACTIVE_THREAD_ID_KEY);
        if (savedThreadId) {
            setActiveThreadId(savedThreadId);
            const savedThreadTitle = getAskAsperStoredState(SELECTED_THREAD_TITLE_KEY);
            if (savedThreadTitle) {
                setSelectedThreadTitle(savedThreadTitle);
            }
        }
    }, [isEvalEnabled, runPromptSync]);

    // Save activeThreadId when it changes
    useEffect(() => {
        setAskAsperStoredState(ACTIVE_THREAD_ID_KEY, activeThreadId);
    }, [activeThreadId]);

    // Save selectedThreadTitle when it changes
    useEffect(() => {
        setAskAsperStoredState(SELECTED_THREAD_TITLE_KEY, selectedThreadTitle);
    }, [selectedThreadTitle]);

    // Keep session-tracked thread titles in sync as names become available (new-thread
    // naming after first reply, renames, sidebar refetch).
    useEffect(() => {
        if (activeThreadId && selectedThreadTitle) {
            updateAskAsperThreadTitle(activeThreadId, selectedThreadTitle);
        }
    }, [activeThreadId, selectedThreadTitle]);

    useEffect(() => {
        chatSessions?.forEach((session) => {
            if (session.thread_id && session.thread_name) {
                updateAskAsperThreadTitle(session.thread_id, session.thread_name);
            }
        });
    }, [chatSessions]);

    // Restore chat history when component mounts with a stored activeThreadId
    useEffect(() => {
        if (hasRestoredRef.current || !activeThreadId) return;
        hasRestoredRef.current = true;

        const restoreThread = async () => {
            try {
                const savedTitle = getAskAsperStoredState(SELECTED_THREAD_TITLE_KEY);
                recordAskAsperThreadAccessed(activeThreadId, savedTitle);
                const history = await getChatHistory(activeThreadId);
                const transformedMessages = transformChatHistory(
                    history as ApiHistoryMessage[]
                );
                setMessages(transformedMessages);
                void loadFeedbackHistory(activeThreadId);
            } catch (error) {
                console.error('Failed to restore chat history:', error);
                notifyAskAsperApiError(error);
            }
        };

        restoreThread();
    }, [activeThreadId, loadFeedbackHistory]);

    const handleNewChat = useCallback(() => {
        setMessages([]);
        setLoadingMessage(null);
        loadingMessageRef.current = null;
        setActiveThreadId(undefined);
        setSelectedThreadTitle(undefined);
        setSearchValue('');
        setFeedbackHistory(undefined);
        if (isEvalEnabled) {
            runPromptSync(selectedTenantRef.current).catch(() => {});
        }
        setSelectedLookups([]);
        refetchSessions();
    }, [refetchSessions, runPromptSync, isEvalEnabled]);

    const clearDeepLinkSearchParams = useCallback(() => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('threadId');
        nextParams.delete('taskId');
        nextParams.delete('runId');
        nextParams.delete('userId');
        const search = nextParams.toString();
        navigate(
            { pathname: ASK_ASPER_ROUTE, search: search ? `?${search}` : '' },
            { replace: true },
        );
    }, [navigate, searchParams]);

    // A deep link belonging to another user: tell them it's unavailable and reset to a new chat.
    const deepLinkMismatchHandledRef = useRef(false);
    useEffect(() => {
        if (!deepLinkUserMismatch) {
            deepLinkMismatchHandledRef.current = false;
            return;
        }
        if (deepLinkMismatchHandledRef.current) return;
        deepLinkMismatchHandledRef.current = true;
        notifyAskAsperArtifactUnavailable();
        clearDeepLinkSearchParams();
        handleNewChat();
    }, [deepLinkUserMismatch, clearDeepLinkSearchParams, handleNewChat]);

    const handleExportDeepLinkConsumed = useCallback(
        (_target: AgenticExportDeepLinkTarget, found: boolean) => {
            clearDeepLinkSearchParams();
            if (!found) {
                console.warn('[AskAsper] export deep link: artefact not found for target');
            }
        },
        [clearDeepLinkSearchParams],
    );

    const handleThreadSelect = useCallback(
        async (threadId: string) => {
            const thread = chatSessions?.find((s) => s.thread_id === threadId);
            recordAskAsperThreadAccessed(threadId, thread?.thread_name);
            setActiveThreadId(threadId);
            setFeedbackHistory(undefined);

            if (thread?.thread_name) {
                setSelectedThreadTitle(thread.thread_name);
            }

            try {
                if (isEvalEnabled) {
                    await runPromptSync(selectedTenantRef.current);
                }
                const history = await getChatHistory(threadId);
                const transformedMessages = transformChatHistory(
                    history as ApiHistoryMessage[]
                );
                setMessages(transformedMessages);
                void loadFeedbackHistory(threadId);
            } catch (error) {
                console.error('Failed to fetch chat history:', error);
                notifyAskAsperApiError(error);
                setMessages([]);
                setFeedbackHistory(undefined);
            }
        },
        [chatSessions, runPromptSync, isEvalEnabled, loadFeedbackHistory],
    );

    // Handle thread edit (rename)
    const handleThreadEdit = useCallback(
        async (threadId: string, newName?: string) => {
            const session = chatSessions?.find((s) => s.thread_id === threadId);
            if (!session) return;

            // If newName is provided, use it; otherwise skip (component should handle UI)
            if (newName && newName.trim() && newName !== session.thread_name) {
                try {
                    await renameThread({
                        thread_id: threadId,
                        new_thread_name: newName.trim(),
                    });
                    // Update selected thread title if this is the active thread
                    if (activeThreadId === threadId) {
                        setSelectedThreadTitle(newName.trim());
                    }
                    // Refetch conversations after rename
                    await refetchSessions();
                } catch (error) {
                    console.error('Failed to rename thread:', error);
                    notifyAskAsperApiError(error);
                }
            }
        },
        [chatSessions, refetchSessions, activeThreadId],
    );

    // Handle thread delete
    const handleThreadDelete = useCallback(
        async (threadId: string) => {
            try {
                await deleteThread(threadId);
                // Refetch conversations after delete
                await refetchSessions();
                if (activeThreadId === threadId) {
                    setActiveThreadId(undefined);
                    setSelectedThreadTitle(undefined);
                    setMessages([]);
                    setLoadingMessage(null);
                    loadingMessageRef.current = null;
                    setFeedbackHistory(undefined);
                }
            } catch (error) {
                console.error('Failed to delete thread:', error);
                notifyAskAsperApiError(error);
            }
        },
        [activeThreadId, refetchSessions],
    );

    const handleViewModeChange = useCallback((_mode: 'list' | 'presentation') => {
        // View mode change handler
        setViewMode(_mode);
    }, [setViewMode]);

    const handleTenantChange = useCallback(
        (value?: string) => {
            const nextTenant = value || undefined;
            setSelectedTenant(nextTenant);
            selectedTenantRef.current = nextTenant;
            setAskAsperStoredState(SELECTED_PROMPT_BRANCH_KEY, nextTenant);
            runPromptSync(nextTenant).catch(() => {});
        },
        [runPromptSync],
    );

    const tenantOptions = tenants.flatMap((t) => {
        const opts = [{ value: t.name, label: t.name }];
        (t.drafts ?? []).forEach((d: string) => {
            opts.push({ value: d, label: `${t.name} → ${d}` });
        });
        return opts;
    });

    const [navbarSlot, setNavbarSlot] = useState<HTMLElement | null>(null);

    useEffect(() => {
        if (!isEvalEnabled) {
            setNavbarSlot(null);
            return undefined;
        }
        // Resolve the navbar slot once it's mounted; retry on the next animation frame if not yet present
        let raf = 0;
        const resolve = () => {
            const el = document.getElementById('navbar-extra-controls');
            if (el) {
                setNavbarSlot(el);
            } else {
                raf = requestAnimationFrame(resolve);
            }
        };
        resolve();
        return () => {
            if (raf) cancelAnimationFrame(raf);
            setNavbarSlot(null);
        };
    }, [isEvalEnabled]);

    const topNavControlsContent = (
        <>
            {tenantOptions.length > 0 && (
                <Select
                    allowClear
                    placeholder="Prompt Branch"
                    style={{ width: 220 }}
                    value={selectedTenant}
                    onChange={handleTenantChange}
                    options={tenantOptions}
                    showSearch
                    size="small"
                    disabled={isStreaming || promptSyncing}
                />
            )}
            <button type="button" style={evalNavStyle} onClick={() => navigate('/ask-asper/eval')}>
                <span style={{ fontSize: 15 }}>&#9881;</span>
                Eval Sets
            </button>
        </>
    );

    const topNavControls =
        isEvalEnabled && navbarSlot ? ReactDOM.createPortal(topNavControlsContent, navbarSlot) : null;

    // Block render until the lookup-columns list has been fetched (cache-hit makes this nearly
    // instantaneous on repeat mounts; first mount may briefly show the loader).
    if (!lookupsReady) {
        return (
            <div className="w-full h-full min-h-[60vh] flex items-center justify-center">
                <Loader size="large" loaderMessage="Loading Ask Asper..." />
            </div>
        );
    }

    if (showLandingPage) {
        return (
            <>
                {topNavControls}
                <LandingPage
                    searchValue={searchValue}
                    onSearchChange={setSearchValue}
                    onSearchSubmit={handleSearchSubmit}
                    threads={threads}
                    sidebarThreadsLoading={sidebarThreadsLoading}
                    activeThreadId={activeThreadId}
                    onNewChat={handleNewChat}
                    onThreadSelect={handleThreadSelect}
                    onThreadEdit={handleThreadEdit}
                    onThreadDelete={handleThreadDelete}
                    user={{ name: userName }}
                    customQuestions={transformedCustomQuestions}
                    questionBankCategories={questionBankCategories}
                    onAddCustomQuestion={handleAddCustomQuestion}
                    onEditCustomQuestion={handleEditCustomQuestion}
                    onDeleteCustomQuestion={handleDeleteCustomQuestion}
                    lookupItems={lookupItems}
                    selectedLookups={selectedLookups}
                    onLookupsChange={setSelectedLookups}
                    lookupDimensionGroups={lookupDimensionGroups}
                    onTrackEvent={handleAskAsperTrackEvent}
                />
            </>
        );
    }

    const twoPanelProps = {
        messages,
        loadingMessage,
        onSendMessage: handleSearchSubmit,
        showTopBar: true,
        breadcrumbs: [
            { label: 'Home', href: '/' },
            { label: 'Ask Asper' },
        ],
        quickActions: [],
        showSidebar: true,
        sidebarThreadsLoading,
        sidebarThreads: threads,
        sidebarActiveThreadId: activeThreadId,
        onSidebarNewChat: handleNewChat,
        onSidebarThreadSelect: handleThreadSelect,
        onSidebarThreadEdit: handleThreadEdit,
        onSidebarThreadDelete: handleThreadDelete,
        viewMode,
        showViewModeToggle: true,
        onViewModeChange: handleViewModeChange,
        disableNavbar: true,
        customQuestions: transformedCustomQuestions,
        questionBankCategories:
            questionBankCategories.length > 0
                ? (toQuestionBankApiFormat(questionBankCategories) as any)
                : undefined,
        onAddCustomQuestion: handleAddCustomQuestion,
        onEditCustomQuestion: handleEditCustomQuestion,
        onDeleteCustomQuestion: handleDeleteCustomQuestion,
        onArtifactExport: handleArtifactExport,
        onMultiSelectExport: handleMultiSelectExport,
        onRightPanelThumbsUp: handleRightPanelThumbsUp,
        onRightPanelThumbsDown: handleRightPanelThumbsDown,
        onRightPanelDislikeFeedbackSubmit: handleRightPanelDislikeFeedbackSubmit,
        feedbackHistory,
        lookupItems,
        selectedLookups,
        onLookupsChange: setSelectedLookups,
        // Dimension groups only — do not pass lookupCategories (library would ignore groups).
        lookupDimensionGroups,
        onEntityDisambiguationContinue: handleEntityDisambiguationContinue,
        onEntityDisambiguationSkip: handleEntityDisambiguationSkip,
        onTrackEvent: handleAskAsperTrackEvent,
        searchDisclaimer: ASK_ASPER_CHAT_SEARCH_DISCLAIMER,
        exportDeepLink,
        exportDeepLinkTarget: effectiveDeepLinkTarget,
        onExportDeepLinkConsumed: handleExportDeepLinkConsumed,
    };
    return (
        <>
            {topNavControls}
            <AgenticTwoPanelLayout {...(twoPanelProps as any)} />
            {isEvalEnabled && <AddToEvalButton messages={messages} activeThreadId={activeThreadId} />}
        </>
    );
};
